import { isPdfAssetUrl } from "../../BrowseDesigns/components/detailsDialogUtils";

export function getAssetKey(asset, index) {
  return asset?._id || asset?.storagePath || asset?.url || `asset-${index}`;
}

export function getAssetKind(asset) {
  if (asset?.kind) {
    return asset.kind;
  }

  return isPdfAssetUrl(asset?.url) ? "pdf" : "image";
}

export function getAssetMimeType(asset) {
  if (typeof asset?.mimeType === "string" && asset.mimeType.trim()) {
    return asset.mimeType.trim().toLowerCase();
  }

  const sourceUrl = asset?.previewUrl || asset?.url || "";
  if (typeof sourceUrl === "string" && sourceUrl.startsWith("data:")) {
    const mimeMatch = sourceUrl.match(/^data:([^;]+);/i);
    if (mimeMatch?.[1]) {
      return mimeMatch[1].trim().toLowerCase();
    }
  }

  if (typeof asset?.fileName === "string" && /\.svg$/i.test(asset.fileName)) {
    return "image/svg+xml";
  }

  if (typeof asset?.url === "string" && /\.svg($|[?#])/i.test(asset.url)) {
    return "image/svg+xml";
  }

  return "";
}

export function isSvgAsset(asset) {
  return getAssetMimeType(asset) === "image/svg+xml";
}

export function getAssetPreviewUrl(asset) {
  const kind = getAssetKind(asset);
  if (kind === "pdf") {
    return asset?.previewUrl || "";
  }

  return asset?.url || "";
}

export function getCategoryAssetCount(category) {
  return Array.isArray(category?.assets) ? category.assets.length : 0;
}

function createEmptyPanel() {
  return {
    cells: Array.from({ length: 2 }, () => Array(3).fill(false)),
    items: [],
  };
}

function hashSeed(value) {
  return String(value || "")
    .split("")
    .reduce((total, char) => total + char.charCodeAt(0), 0);
}

function canPlace(panel, column, row, width, height) {
  if (column + width > 3 || row + height > 2) {
    return false;
  }

  for (let y = row; y < row + height; y += 1) {
    for (let x = column; x < column + width; x += 1) {
      if (panel.cells[y][x]) {
        return false;
      }
    }
  }

  return true;
}

function markPlacement(panel, column, row, width, height, item) {
  for (let y = row; y < row + height; y += 1) {
    for (let x = column; x < column + width; x += 1) {
      panel.cells[y][x] = true;
    }
  }

  panel.items.push({
    ...item,
    column,
    row,
    width,
    height,
  });
}

function dedupeSpans(spans) {
  const seen = new Set();
  return spans.filter((span) => {
    const key = `${span.width}x${span.height}`;
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function getPreferredSpans(shape, assetIndex, assetKey) {
  const seed = hashSeed(`${assetKey}-${assetIndex}`);
  const preferLarge = seed % 3 !== 0;
  const preferCompact = seed % 5 === 0;

  if (shape === "pdf" || shape === "vertical") {
    return dedupeSpans([
      preferLarge ? { width: 1, height: 2 } : { width: 1, height: 1 },
      preferCompact ? { width: 1, height: 1 } : { width: 1, height: 2 },
      { width: 2, height: 2 },
      { width: 2, height: 1 },
    ]);
  }

  if (shape === "square") {
    return dedupeSpans([
      preferLarge ? { width: 2, height: 2 } : { width: 1, height: 1 },
      { width: 1, height: 1 },
      { width: 2, height: 1 },
      { width: 1, height: 2 },
    ]);
  }

  return dedupeSpans([
    preferLarge ? { width: 2, height: 1 } : { width: 1, height: 1 },
    preferCompact ? { width: 1, height: 1 } : { width: 2, height: 1 },
    { width: 2, height: 2 },
    { width: 1, height: 2 },
  ]);
}

function placeIntoPanel(panel, item) {
  const spanOptions = getPreferredSpans(item.shape, item.assetIndex, item.assetKey);

  for (const span of spanOptions) {
    for (let row = 0; row < 2; row += 1) {
      for (let column = 0; column < 3; column += 1) {
        if (canPlace(panel, column, row, span.width, span.height)) {
          markPlacement(panel, column, row, span.width, span.height, item);
          return true;
        }
      }
    }
  }

  return false;
}

function fillPanelFromStart(panel, items) {
  if (!items.length) {
    return;
  }

  let cursor = 0;
  let safety = 0;

  while (cursor < items.length * 4 && safety < items.length * 6) {
    const sourceItem = items[cursor % items.length];
    const duplicateItem = {
      ...sourceItem,
      isLoopFill: true,
    };

    if (placeIntoPanel(panel, duplicateItem)) {
      if (panel.cells.every((row) => row.every(Boolean))) {
        return;
      }
    }

    cursor += 1;
    safety += 1;
  }
}

export function buildGalleryPanels(assets, shapeMap) {
  const panels = [];
  const orderedItems = [];
  let currentPanel = createEmptyPanel();

  assets.forEach((asset, assetIndex) => {
    const assetKey = getAssetKey(asset, assetIndex);
    const item = {
      asset,
      assetIndex,
      assetKey,
      shape: shapeMap[assetKey] || getAssetKind(asset),
    };

    if (!placeIntoPanel(currentPanel, item)) {
      fillPanelFromStart(currentPanel, orderedItems);
      panels.push(currentPanel);
      currentPanel = createEmptyPanel();
      placeIntoPanel(currentPanel, item);
    }

    orderedItems.push(item);
  });

  if (currentPanel.items.length > 0) {
    fillPanelFromStart(currentPanel, orderedItems);
    panels.push(currentPanel);
  }

  return panels;
}
